// core
import React, { useContext, useState } from "react";

// components
import LModal from "../../../../common/components/Modals/LModal/LModal";
import ModalHeader from "../../../../common/components/Modals/ModalHeader/ModalHeader";

// providers
import Dropdown from "../../../../common/components/Forms/Dropdown/Dropdown";
import { IDropdownOption } from "../../../../common/interfaces/IDropdownOption";
import Alert from "../../../../common/components/Alert/Alert";
import { Severity } from "../../../../common/interfaces/enums/Severity";
import Input from "../../../../common/components/Forms/Input/Input";
import { Form, Formik } from "formik";
import Button from "../../../../common/components/Forms/Button/Button";
import * as Yup from "yup";
import Icon from "../../../../common/components/Icon/Icon";
import { QuoteGroupContext } from "../../../providers/RootContextProvider";
import cx from "classnames";
import styles from "./ContactUsModal.module.scss";
import LAccordion from "../../LAccordion/LAccordion";
import useAccordion from "../../../../common/hooks/useAccordion";
import SummaryWrapper from "../../SummaryWrapper/SummaryWrapper";
import AcknowledgementModal from "../../../../common/components/Modals/AcknowledgementModal/AcknowledgementModal";
import { sendContactEmail } from "../../../api/endpoints/ApiEmail";

type ModalOptions =
    | "visit"
    | "help"
    | "update"
    | "cancel"
    | "personal"
    | "claim"
    | "other";

const MAX_CHARACTER_COUNT = 500;

const EnquiryOptions: IDropdownOption<ModalOptions>[] = [
    { label: "Visiting New Zealand or I have a visa and want insurance", value: "visit" },
    { label: "Help with an insurance quote", value: "help" },
    { label: "Update an existing policy", value: "update" },
    { label: "Cancelling a policy", value: "cancel" },
    { label: "Updating my personal info", value: "personal" },
    { label: "Make a claim", value: "claim" },
    { label: "Other", value: "other" }
];

interface IContactUsModal {
    setEnquiry?: ModalOptions;
    isOpen: boolean;
    onClose: () => void;
}

const OpeningHours: {
    day: string;
    time: string;
}[] = [
    {
        day: "Monday",
        time: "8.30am - 5.30pm"
    },
    {
        day: "Tuesday",
        time: "8.30am - 5.30pm"
    },
    {
        day: "Wednesday",
        time: "8.30am - 5.30pm"
    },
    {
        day: "Thursday",
        time: "8.30am - 5.30pm"
    },
    {
        day: "Friday",
        time: "8.30am - 5.00pm"
    },
    {
        day: "Saturday",
        time: "closed"
    },
    {
        day: "Sunday",
        time: "closed"
    }
];

const validaton = Yup.object().shape({
    enquiry: Yup.string().required("Please tell us what you are enquiring about"),
    details: Yup.string()
        .max(MAX_CHARACTER_COUNT, "Please keep your details to 500 characters or less")
        .required("Please tell us how we can help you"),
    email: Yup.string()
        .required("Please tell us your email, so we can get back to you.")
        .email("Please make sure your email is valid")
});

const renderNZVisitor = (): React.ReactNode => {
    return (
        <Alert severity={Severity.Error}>
            <p>Not a New Zealand citizen?</p>
            <p>
                To cut to the chase, we don&apos;t have any insurance policies that will
                cover you.
            </p>
            <p>
                There&apos;s good news though! If you have less than 12 months remaining
                on your visa, or if you&apos;re traveling short-term, here are some
                products we trust:
            </p>
            <ul>
                <li>
                    <a
                        href="https://quote.accuro.co.nz/?bc=102"
                        target="_blank"
                        rel="noopener">
                        SmartStay
                    </a>
                    &nbsp;by Accuro, or;
                </li>
                <li>
                    <a
                        href="https://www.scti.co.nz/our-policies/visiting-new-zealand/insurance-cover"
                        target="_blank"
                        rel="noopener">
                        Visiting NZ Insurance
                    </a>
                    &nbsp;by Southern Cross
                </li>
            </ul>
        </Alert>
    );
};

const ContactUsModal: React.FC<IContactUsModal> = (props) => {
    const { setEnquiry, isOpen, onClose } = props;

    const { selectors } = useContext(QuoteGroupContext);

    const contactDetails = selectors.getContactDetails();

    const [isLoading, setLoading] = useState(false);
    const [isAcknowledged, setIsAcknowledged] = useState(false);
    const [successState, setSuccessState] = useState(false);

    const { sectionIsOpen, handleChangeSection } = useAccordion();

    if (isAcknowledged) {
        return (
            <AcknowledgementModal
                isOpen={isOpen}
                hasSucceeded={successState}
                onClose={() => {
                    setIsAcknowledged(false);
                    if (successState) {
                        onClose();
                    }
                }}
                successContent={
                    <React.Fragment>
                        <div className="gap-ml">Thank you for your enquiry</div>

                        <p className="gap-ml text-font-size-s">
                            We aim to get back to you within 2 business days. If your
                            query is urgent, please call us on{" "}
                            <a href="tel:+64800800400">0800 800 400</a>.
                        </p>
                    </React.Fragment>
                }
                iconId="DogThanks"
            />
        );
    }

    return (
        <React.Fragment>
            <LModal
                Header={() => <ModalHeader onClose={onClose} title="Need support?" />}
                calcHeightMode="auto"
                width="wide"
                isOpen={isOpen}
                onClose={onClose}
                isLoading={isLoading}>
                {() => (
                    <React.Fragment>
                        <div className="layout-flex-column">
                            <h4 className="gap-l">
                                Don&#39;t we all? Luckily, we have a friendly crew based
                                in Wellington ready to help.
                            </h4>

                            <Formik
                                initialValues={{
                                    enquiry: setEnquiry || "",
                                    details: "",
                                    email: contactDetails.emailAddress || ""
                                }}
                                onSubmit={(values) => {
                                    setLoading(true);

                                    const enquiryString = EnquiryOptions.find(
                                        (item) => item.value === values.enquiry
                                    ).label;

                                    sendContactEmail({
                                        Location: "Multi-compare - Contact Us",
                                        GroupId: selectors.getGroupId(),
                                        Subject: `LifeDirect Website Query - ${enquiryString}`,
                                        Enquiry: enquiryString,
                                        Body: values.details,
                                        Email: values.email,
                                        ClientName: "",
                                        Phone: ""
                                    })
                                        .then(() => setSuccessState(true))
                                        .catch(() => setSuccessState(false))
                                        .finally(() => {
                                            setIsAcknowledged(true);
                                            setLoading(false);
                                        });
                                }}
                                validationSchema={validaton}>
                                {({
                                    values,
                                    touched,
                                    errors,
                                    handleChange,
                                    handleBlur,
                                    isValid,
                                    isSubmitting,
                                    submitCount
                                }) => (
                                    <Form
                                        className={cx(
                                            styles["contact-us--container"],
                                            "gap-xl"
                                        )}>
                                        <h3 className="gap-l">Message us</h3>
                                        <Dropdown
                                            id="enquiry"
                                            name="enquiry"
                                            options={EnquiryOptions}
                                            selectedOptionId={values.enquiry}
                                            placeholder="Please select a subject"
                                            title="I am enquiring about"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            hasError={touched.enquiry && errors.enquiry}
                                        />

                                        {values.enquiry === "visit" && renderNZVisitor()}
                                        <Input
                                            name="details"
                                            type="textarea"
                                            value={values.details}
                                            label="Details"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            rows={5}
                                            maxlength={MAX_CHARACTER_COUNT}
                                            hasError={touched.details && errors.details}>
                                            <span className="text-light text-font-size-xs">
                                                {MAX_CHARACTER_COUNT -
                                                    values.details.length}{" "}
                                                characters remaining
                                            </span>
                                        </Input>
                                        <Input
                                            name="email"
                                            type="email"
                                            value={values.email}
                                            label="Your email address"
                                            placeholder="name@email.com"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            className="gap-l"
                                            hasError={touched.email && errors.email}
                                        />
                                        <div
                                            className={
                                                styles["contact-us--form__button-row"]
                                            }>
                                            <Button
                                                component="button"
                                                type="submit"
                                                size="m"
                                                variant="calltoaction"
                                                isDisabled={
                                                    submitCount > 0 &&
                                                    (!isValid || isSubmitting)
                                                }
                                                isSubmitting={isSubmitting}>
                                                Send Message
                                            </Button>
                                        </div>
                                    </Form>
                                )}
                            </Formik>

                            <div
                                className={cx(
                                    styles["contact-us--footer-container"],
                                    "gap-l"
                                )}>
                                <div className={styles["contact-us--footer-row"]}>
                                    <Icon
                                        id="Email"
                                        className={styles["contact-us--footer-row__icon"]}
                                    />

                                    <div
                                        className={
                                            styles["contact-us--footer-row-content"]
                                        }>
                                        <h3
                                            className={
                                                styles[
                                                    "contact-us--footer-row-content__title"
                                                ]
                                            }>
                                            <a href="mailto:team@lifedirect.co.nz">
                                                team@lifedirect.co.nz
                                            </a>
                                        </h3>
                                    </div>
                                </div>
                                <div
                                    className={cx(
                                        styles["contact-us--footer-row"],
                                        "gap-xl"
                                    )}>
                                    <Icon
                                        id="Phone"
                                        className={styles["contact-us--footer-row__icon"]}
                                    />

                                    <div
                                        className={
                                            styles["contact-us--footer-row-content"]
                                        }>
                                        <h3
                                            className={
                                                styles[
                                                    "contact-us--footer-row-content__title"
                                                ]
                                            }>
                                            <a href="tel:0800800400">0800 800 400</a>
                                        </h3>
                                        <div
                                            className={
                                                styles[
                                                    "contact-us--footer-row-content__right"
                                                ]
                                            }>
                                            <span>Overseas? Call&nbsp;</span>
                                            <span>
                                                <a href="tel:+6498887025">
                                                    +64 9 888 7025
                                                </a>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <LAccordion
                                    name="MailingAddress"
                                    expanded={sectionIsOpen("MailingAddress")}
                                    handleChange={handleChangeSection}
                                    size="small"
                                    summary={() => {
                                        return (
                                            <SummaryWrapper>
                                                <Icon
                                                    id="Address"
                                                    className={
                                                        styles[
                                                            "contact-us--footer-row__icon"
                                                        ]
                                                    }
                                                />

                                                <h3
                                                    className={
                                                        styles[
                                                            "contact-us--footer-row-content"
                                                        ]
                                                    }>
                                                    Mailing Address
                                                </h3>
                                            </SummaryWrapper>
                                        );
                                    }}>
                                    Freepost 232299 <br />
                                    PO Box 11050 <br />
                                    Manners Street <br />
                                    Wellington 6142
                                </LAccordion>
                                <LAccordion
                                    name="OpeningHours"
                                    expanded={sectionIsOpen("OpeningHours")}
                                    handleChange={handleChangeSection}
                                    size="small"
                                    summary={() => {
                                        return (
                                            <SummaryWrapper>
                                                <Icon
                                                    id="OpenHours"
                                                    className={
                                                        styles[
                                                            "contact-us--footer-row__icon"
                                                        ]
                                                    }
                                                />

                                                <h3
                                                    className={
                                                        styles[
                                                            "contact-us--footer-row-content"
                                                        ]
                                                    }>
                                                    Opening Hours
                                                </h3>
                                            </SummaryWrapper>
                                        );
                                    }}>
                                    <div className={styles["contact-us--open-hours"]}>
                                        {OpeningHours.map((row, index) => {
                                            return (
                                                <div
                                                    className={
                                                        styles[
                                                            "contact-us--open-hours__row"
                                                        ]
                                                    }
                                                    key={row.day}>
                                                    <span
                                                        style={{
                                                            gridRow: index + 1,
                                                            gridColumn: 1
                                                        }}>
                                                        {row.day}
                                                    </span>
                                                    <span
                                                        style={{
                                                            gridRow: index + 1,
                                                            gridColumn: 2
                                                        }}>
                                                        {row.time}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </LAccordion>
                            </div>
                            <div className="text-light text-font-size-xs">
                                LifeDirect 2020 Limited (FSP724353) trading as LifeDirect
                                is a Financial Advice Provider. We employ Financial
                                Advisers and you can contact us on{" "}
                                <a href="tel:0800800400">0800 800 400</a>, at{" "}
                                <a href="mailto:team@lifedirect.co.nz">
                                    team@lifedirect.co.nz
                                </a>{" "}
                                or in writing at PO Box 11050, Manners St, Wellington
                                6142.
                            </div>
                        </div>
                    </React.Fragment>
                )}
            </LModal>
        </React.Fragment>
    );
};

export default ContactUsModal;
